import { db } from '@/api/firebase';
import { useMainContext } from '@/context/main-context';
import { collection, getDocs, query, serverTimestamp, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';

export function useLeads() {
  const { user } = useMainContext();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // leadlarni olish
  const getLeads = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const q = query(collection(db, 'leads'), where('userId', '==', user.uid));
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map((docSnap) => ({
        ...docSnap.data(),
        leadId: docSnap.id,
      }));
      setLeads(list);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getLeads();
  }, [user]);

  // faqat query helperlar
  const getRequestsByLead = async (leadId) => {
    const q = query(collection(db, 'requests'), where('leadId', '==', leadId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  };

  const getRequestsByColumn = async (columnId) => {
    const q = query(
      collection(db, 'requests'),
      where('columnId', '==', columnId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  };

  const getRequestsByLeadAndColumn = async (leadId, columnId) => {
    const q = query(
      collection(db, 'requests'),
      where('leadId', '==', leadId),
      where('columnId', '==', columnId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((docSnap) => ({
      ...docSnap.data(),
      requestId: docSnap.id,
    }));
  };

  // Lead CRUD
  const addLead = async (name) => {
    if (!user) return;
    setLoading(true);
    try {
      await addDoc(collection(db, 'leads'), {
        name,
        userId: user.uid,
        columns: [
          {
            id: generateId(),
            title: 'New Leads',
            leadIds: [],
          },
        ],
        createdAt: serverTimestamp(),
      });
      await getLeads();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const editLead = async (leadId, updatedData) => {
    setLoading(true);
    try {
      const leadRef = doc(db, 'leads', leadId);
      await updateDoc(leadRef, {
        ...updatedData,
        updatedAt: serverTimestamp(),
      });
      await getLeads();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addColumn = async (leadId, columnName, color = '#ccc') => {
    setLoading(true);
    try {
      const lead = leads.find((l) => l.leadId === leadId);
      if (!lead) throw new Error('Lead topilmadi');

      const updatedColumns = [
        ...lead.columns,
        {
          id: generateId(),
          title: columnName,
          color,
          leadIds: [],
        },
      ];

      const leadRef = doc(db, 'leads', leadId);
      await updateDoc(leadRef, {
        columns: updatedColumns,
        updatedAt: serverTimestamp(),
      });
      await getLeads();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteLead = async (leadId) => {
    setLoading(true);
    try {
      await deleteDoc(doc(db, 'leads', leadId));
      await getLeads();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    leads,
    loading,
    error,
    addLead,
    editLead,
    addColumn,
    deleteLead,
    getLeads,
    getRequestsByLead,
    getRequestsByColumn,
    getRequestsByLeadAndColumn,
  };
}
